import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS } from '@shared/roles';
import { api } from '../api';
import { homeFor, useSession } from '../session';
import { Badge, Empty, ErrorBox, Field, Loading, dt, useBusy } from '../ui';
import { GoogleSignIn, useAuthConfig } from './Login';
import { AcceptInvitation, PendingInvitations, PhoneVerify, type Direction } from './shared/Onboarding';
import { ThemeSettings } from '../theme';

export function Choose() {
  const { me, choose } = useSession();
  const nav = useNavigate();
  if (!me) return <Loading />;
  return (
    <div className="stack">
      <PendingInvitations invitations={me.pendingInvitations} />
      <div className="card">
        <h1>Choose your business</h1>
        {!me.memberships.length ? (
          <Empty>
            You are signed in as {me.user.name}, but you are not part of a business yet.
            <br />
            <Link to="/welcome">Confirm your mobile or register your business</Link>
          </Empty>
        ) : (
          <ul className="list">
            {me.memberships.map((m) => (
              <li key={m.orgId} className="row between">
                <div>
                  <strong>{m.orgName}</strong>
                  <div><small>{m.roles.map((r) => ROLE_LABELS[r]).join(', ')}</small></div>
                </div>
                <button className="btn primary" onClick={() => { choose(m.orgId); nav(homeFor(m)); }}>Open</button>
              </li>
            ))}
          </ul>
        )}
        {me.memberships.length && !me.station ? <p><Link to="/welcome">Register another business</Link></p> : null}
      </div>
    </div>
  );
}

interface InviteInfo {
  fromName: string;
  businessName: string;
  contactName: string;
  phoneMasked: string;
  direction: Direction;
  expiresAt: string;
}

export function Invite() {
  const { token } = useParams();
  const { me, refresh } = useSession();
  const cfg = useAuthConfig();
  const info = useQuery({ queryKey: ['invite', token], queryFn: () => api.get<InviteInfo>(`/api/invitations/${token}`, { orgId: null }) });
  if (info.isLoading) return <Loading />;
  if (info.error) return <div className="login-wrap"><ErrorBox error={info.error} /></div>;
  const i = info.data!;
  const toPump = i.direction === 'BUYER_INVITES_SUPPLIER';
  return (
    <div className="login-wrap" style={{ maxWidth: 560 }}>
      <div className="card stack">
        <h1>Invitation from {i.fromName}</h1>
        <p>
          {toPump
            ? `${i.fromName} buys fuel from your pump and wants to keep its credit records on SuperIndent. The company issues indents to its drivers; you fill fuel and bill it on the credit terms you set.`
            : `${i.fromName} wants to keep your fuel credit records on SuperIndent. Your company will issue indents to its drivers; the pump fills fuel and bills you on the agreed credit terms. You pay the pump directly, as you do today.`}
        </p>
        <p className="muted">Sent to {i.phoneMasked} for <strong>{i.businessName}</strong>.</p>
        {!me ? (
          <>
            <h2>1. Sign in</h2>
            {cfg.data ? <GoogleSignIn config={cfg.data} onDone={() => void refresh()} /> : <Loading />}
          </>
        ) : me.station ? (
          <p className="alert warn">This is a shared pump device. Open the invitation on your own phone.</p>
        ) : !me.user.phoneVerified ? (
          <>
            <h2>2. Confirm your mobile number</h2>
            <PhoneVerify hint={`Use the number this invitation was sent to (${i.phoneMasked}).`} onVerified={() => refresh().then(() => undefined)} />
          </>
        ) : (
          <>
            <h2>{toPump ? 'Your pump' : 'Your business'}</h2>
            <AcceptInvitation
              direction={i.direction}
              defaultName={i.businessName}
              accept={(body) => api.post(`/api/invitations/${token}/accept`, body, { orgId: null })}
            />
          </>
        )}
      </div>
    </div>
  );
}

export function Account() {
  const { me, active, refresh } = useSession();
  const [pin, setPin] = useState('');
  const pinBusy = useBusy();
  const alertsBusy = useBusy();
  const [msg, setMsg] = useState('');
  if (!me) return <Loading />;
  const isStaff = active?.roles.some((r) => r.startsWith('SUPPLIER_'));
  return (
    <div className="stack">
      <div className="card">
        <h1>My account</h1>
        <p><strong>{me.user.name}</strong> {me.user.email ? <span className="muted">· {me.user.email}</span> : null}</p>
        <p>Mobile: {me.user.phoneMasked || '—'} {me.user.phoneVerified ? <Badge tone="ok">verified</Badge> : <Badge tone="warn">not verified</Badge>}</p>
      </div>
      <ThemeSettings />
      {!me.station && (me.notificationSettings?.whatsappEnabled || me.memberships.some((m) => m.roles.some((r) => r === 'BUYER_ADMIN' || r === 'SUPPLIER_ADMIN'))) ? (
        <div className="card stack">
          <h2>Business alerts</h2>
          <p>Enable notifications in the Android app to hear about fuel fills and important account updates. We use Android notifications first.</p>
          <label className="check"><input type="checkbox" checked={Boolean(me.notificationSettings?.whatsappEnabled)} disabled={alertsBusy.busy || !me.user.phoneVerified} onChange={(e) => { const whatsappEnabled = e.target.checked; void alertsBusy.run(async () => { await api.patch('/api/me/notification-preferences', { whatsappEnabled }, { orgId: null }); await refresh(); }); }} /> Send me WhatsApp alerts when Android notifications are not set up.</label>
          <p className="muted">Only credit terms to review, payments to check, and one overdue-bill reminder a day after 9 am. At most 5 WhatsApp alerts a day, sent to your confirmed number. Fuel fills and setup steps (invitation or terms accepted) stay in the app and Android notifications. You can turn this off here at any time.</p>
          {!me.notificationSettings?.whatsappAvailable ? <p className="muted">WhatsApp delivery is not available yet. Your preference will be saved.</p> : null}
          <ErrorBox error={alertsBusy.error} />
        </div>
      ) : null}
      {!me.user.phoneVerified && !me.station ? (
        <div className="card">
          <h2>Confirm your mobile number</h2>
          <PhoneVerify
            hint="If your company or pump added you by this number, your access appears once it is confirmed. Add only your own number."
            onVerified={() => refresh().then(() => undefined)}
          />
        </div>
      ) : null}
      {isStaff && active && !me.station ? (
        <div className="card">
          <h2>Your staff PIN</h2>
          <p className="muted">Use this PIN to select your name on the pump’s shared device before entering fuel given. Each entry will show your name. Do not share your PIN.</p>
          <form className="row" onSubmit={(e) => { e.preventDefault(); void pinBusy.run(async () => { await api.post('/api/me/pin', { orgId: active.orgId, pin }, { orgId: null }); setPin(''); setMsg('PIN saved.'); await refresh(); }); }}>
            <input inputMode="numeric" type="password" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} placeholder="4–6 digits" style={{ maxWidth: 160 }} autoComplete="new-password" />
            <button className="btn primary" disabled={pinBusy.busy || pin.length < 4}>{active.pinSet ? 'Change PIN' : 'Set PIN'}</button>
          </form>
          {msg ? <div className="alert ok">{msg}</div> : null}
          <ErrorBox error={pinBusy.error} />
        </div>
      ) : null}
    </div>
  );
}

interface Notice {
  id: string;
  title: string;
  body: string;
  link?: string;
  readAt?: string;
  createdAt: string;
}

export function Notifications() {
  const qc = useQueryClient();
  const { refresh } = useSession();
  const q = useQuery({ queryKey: ['notifications'], queryFn: () => api.get<Notice[]>('/api/notifications', { orgId: null }) });
  const { can } = useSession();
  const canSet = can('org.members.manage', 'supplier.staff.manage');
  const org = useQuery({ queryKey: ['org'], queryFn: () => api.get<{ settings: { dailyDigest?: boolean } }>('/api/org'), enabled: canSet });
  const toggleDigest = async (on: boolean) => {
    await api.patch('/api/org/settings', { dailyDigest: on });
    await qc.invalidateQueries({ queryKey: ['org'] });
  };
  const markAll = async () => {
    await api.post('/api/notifications/read', {}, { orgId: null });
    await qc.invalidateQueries({ queryKey: ['notifications'] });
    await refresh();
  };
  return (
    <div className="card">
      <div className="row between">
        <h1>Notifications</h1>
        <button className="btn small" onClick={() => void markAll()}>Mark all read</button>
      </div>
      {canSet && org.data ? (
        <label className="check"><input type="checkbox" checked={Boolean(org.data.settings.dailyDigest)} onChange={(e) => void toggleDigest(e.target.checked)} /> Send our business a daily summary at 7 pm</label>
      ) : null}
      {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
      {q.data && !q.data.length ? <Empty>No notifications yet.</Empty> : null}
      <ul className="list">
        {q.data?.map((n) => (
          <li key={n.id} style={{ opacity: n.readAt ? 0.65 : 1 }}>
            {n.link ? <Link to={n.link}><strong>{n.title}</strong></Link> : <strong>{n.title}</strong>}
            <div>{n.body}</div>
            <small>{dt(n.createdAt)}</small>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DevInbox() {
  const q = useQuery({ queryKey: ['dev-inbox'], queryFn: () => api.get<{ id: string; to: string; purpose: string; text: string; createdAt: string }[]>('/api/dev/inbox', { orgId: null }), refetchInterval: 3000 });
  return (
    <div className="login-wrap" style={{ maxWidth: 720 }}>
      <div className="card">
        <h1>Local SMS inbox</h1>
        <p className="alert warn">Development only. In production, messages go through MessageCentral and are never shown here.</p>
        {q.error ? <ErrorBox error={q.error} /> : null}
        <ul className="list">
          {q.data?.map((m) => (
            <li key={m.id}>
              <div className="row between"><strong>{m.to}</strong><Badge>{m.purpose}</Badge></div>
              <div className="mono">{m.text}</div>
              <small>{dt(m.createdAt)}</small>
            </li>
          ))}
        </ul>
        <Link to="/">Back to SuperIndent</Link>
      </div>
    </div>
  );
}
